import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Shell-level: scoped transitions. Transform allowed for active:scale effects.
const buttonVariants = cva(
  "appearance-none inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-[background-color,border-color,color,opacity,box-shadow,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/10 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-gradient-to-r from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] text-[hsl(var(--primary-foreground))] hover:shadow-md hover:brightness-105",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-md",
        outline: "border border-primary bg-background text-primary hover:bg-primary hover:text-primary-foreground",
        secondary: "bg-secondary text-secondary-foreground border border-input hover:bg-secondary/80",
        ghost: "bg-transparent text-foreground hover:bg-accent",
        "ghost-utility": "bg-transparent text-[hsl(var(--primary-foreground))]/50 hover:text-[hsl(var(--primary-foreground))]/90 hover:bg-[hsl(var(--primary-foreground))]/5",
        link: "text-primary underline-offset-4 hover:underline",
        success: "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:brightness-95",
        glass: "bg-white/15 text-white border border-white/25 hover:bg-white/25",
        nav: "bg-transparent text-white/90 hover:text-white hover:bg-white/10",
      },
      size: {
        default: "h-10 px-3 py-2 md:px-6 md:py-3",
        sm: "h-9 px-2.5 py-1.5 md:px-4 text-sm",
        lg: "h-11 px-4 py-2.5 md:px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
